import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../../../../config/api";
import { decryptJson } from "../../../../middleware/encryptionMiddleware";

export const uploadFile = createAsyncThunk(
  "fileUpload/uploadFile",
  async ({ file, folder = "uploads" }, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      // Handle both single file and multiple files
      if (Array.isArray(file)) {
        // Multiple files
        file.forEach((f) => formData.append("files", f));
      } else {
        // Single file
        formData.append("files", file);
      }

      if (folder) {
        formData.append("folder", folder);
      }

      const normalizeFileEntry = (entry, fallbackName) => {
        if (!entry) {
          return {
            filename: fallbackName || "",
            url: "",
          };
        }

        if (typeof entry === "string") {
          return {
            filename: fallbackName || "",
            url: entry,
          };
        }

        const filename =
          entry.originalname ||
          entry.filename ||
          entry.name ||
          entry.fileName ||
          fallbackName ||
          "";

        const url =
          entry.url ||
          entry.fileUrl ||
          entry.Location ||
          entry.location ||
          entry.secure_url ||
          entry.signedUrl ||
          entry.path ||
          entry.link ||
          "";

        return { filename, url };
      };

      const normalizeFiles = (payload, fallbackName) => {
        if (!payload) {
          return [];
        }

        if (Array.isArray(payload)) {
          return payload
            .map((item) => normalizeFileEntry(item, fallbackName))
            .filter((item) => item.url);
        }

        const single = normalizeFileEntry(payload, fallbackName);
        return single.url ? [single] : [];
      };

      const buildReturnValue = (filesList) => {
        if (Array.isArray(file)) {
          return {
            fileUrl: filesList.map(({ url, filename }) => ({
              url,
              filename,
            })),
          };
        }

        const firstFile = filesList[0] || {};
        return {
          filename: firstFile.filename || file?.name || "",
          fileUrl: firstFile.url || "",
        };
      };

      // Use regular axios for file uploads since encryption doesn't work with FormData
      // The backend will handle the response encryption separately
      const response = await axios.post(`${BASE_URL}/v1/upload/s3`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response?.data?.data && Array.isArray(response.data.data)) {
        const normalized = normalizeFiles(response.data.data, file?.name);
        return buildReturnValue(normalized);
      }

      // Check if response is encrypted and decrypt it
      if (
        response.data &&
        response.data.vi &&
        response.data.iv &&
        response.data.content
      ) {
        try {
          const decryptedData = decryptJson(response.data);

          // Now handle the decrypted response based on its structure
          if (decryptedData.files && Array.isArray(decryptedData.files)) {
            const uploadedFiles = normalizeFiles(
              decryptedData.files,
              file?.name
            );
            return buildReturnValue(uploadedFiles);
          } else if (decryptedData.url) {
            const normalized = normalizeFiles(decryptedData.url, file?.name);
            return buildReturnValue(normalized);
          } else if (decryptedData.file) {
            const normalized = normalizeFiles(decryptedData.file, file?.name);
            return buildReturnValue(normalized);
          } else if (typeof decryptedData === "string") {
            const normalized = normalizeFiles(decryptedData, file?.name);
            return buildReturnValue(normalized);
          } else {
            const possible =
              decryptedData.fileUrl ||
              decryptedData.path ||
              decryptedData.data ||
              decryptedData.files;
            const normalized = normalizeFiles(possible, file?.name);
            return buildReturnValue(normalized);
          }
        } catch (decryptError) {
          console.error("Failed to decrypt response:", decryptError);
          // If decryption fails, try to use the raw response
        }
      }

      // If we get here, either the response wasn't encrypted or decryption failed
      // Try to extract the URL from the raw response
      if (response.data.url) {
        const normalized = normalizeFiles(response.data.url, file?.name);
        return buildReturnValue(normalized);
      } else {
        const normalized = normalizeFiles(response.data, file?.name);
        return buildReturnValue(normalized);
      }
    } catch (error) {
      console.error("File upload error:", error);
      return rejectWithValue(error.response?.data?.msg || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

const initialState = {
  files: {
    ownerProfileImage: null,
    CACRegistrationCertificateImage: null,
    identificationImage: null,
  },
  previews: {
    ownerProfileImage: null,
    CACRegistrationCertificateImage: null,
    identificationImage: null,
  },
  isLoading: false,
  error: null,
};

const fileUploadSlice = createSlice({
  name: "fileUpload",
  initialState,
  reducers: {
    resetFiles: () => initialState,
    clearFile: (state, action) => {
      state.files[action.payload] = null;
      state.previews[action.payload] = null;
    },
    loadCachedData: (state, action) => {
      return {
        ...state,
        ...action.payload,
        isOfflineData: true,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadFile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(uploadFile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.previews[action.payload.fileType] = action.payload.preview; // Set the base64 preview
      })
      .addCase(uploadFile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { resetFiles, clearFile } = fileUploadSlice.actions;

// Selectors
export const selectFileUploadState = (state) => state.fileUpload;
export const selectFiles = (state) => state.fileUpload.files;
export const selectPreviews = (state) => state.fileUpload.previews;
export const selectFileUploadLoading = (state) => state.fileUpload.isLoading;
export const selectFileUploadError = (state) => state.fileUpload.error;

export default fileUploadSlice.reducer;
